// HOME-DASH: the person's Home layout (`me/prefs/home_layout`), with edits shown at once and
// saved one at a time. Each edit writes the whole value, so two saves in flight could land out of
// order and leave the older one stored; here a save waits for the one before it, and edits made
// meanwhile collapse into the newest. The queue lives beside the query cache, one per person and
// company, not in the component: leaving Home and coming back, or switching company, never
// starts a second save beside one still running, and a save always goes to the company it was
// made in. A queue belongs to the cache entry it was started on: once that entry is gone
// (sign-out clears the cache), nothing queued is sent and nothing that answers late touches the
// cache. While a save is pending or queued, a read keeps the edited value (it never puts an older
// layout back under an edit); an idle read refreshes the stored value a failed save restores.
// Edits wait until the stored value has been read successfully (a failed read offers a retry,
// never an overwrite from the defaults), and a read in flight is cancelled before each edit.
import { type Query, type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { type HomeLayoutPref, p1Keys } from "../work/task-page/queries";

type Callbacks = { onSuccess?: () => void; onError?: (e: unknown) => void };

type Queue = {
  busy: boolean;
  next: { value: HomeLayoutPref; callbacks: Callbacks[] } | null;
  /** The last value the API holds (from an idle read or a confirmed save). */
  stored: HomeLayoutPref | null;
  /** The cache entry the queue serves; another one (after a clear) ends the queue. */
  query: Query | undefined;
  /** Edits made so far: a read that began before an edit never becomes the stored value. */
  edits: number;
};

/** Query client → `${person}:${company}` → its save queue. */
const QUEUES = new WeakMap<QueryClient, Map<string, Queue>>();

function queueFor(qc: QueryClient, key: string): Queue {
  let byKey = QUEUES.get(qc);
  if (!byKey) {
    byKey = new Map();
    QUEUES.set(qc, byKey);
  }
  let q = byKey.get(key);
  if (!q) {
    q = { busy: false, next: null, stored: null, query: undefined, edits: 0 };
    byKey.set(key, q);
  }
  return q;
}

const entryOf = (qc: QueryClient, co: string) =>
  qc.getQueryCache().find({ queryKey: p1Keys.pref(co, "home_layout"), exact: true });

function send(
  qc: QueryClient,
  api: Api,
  co: string,
  s: Queue,
  value: HomeLayoutPref,
  callbacks: Callbacks[],
): void {
  s.busy = true;
  // The cache entry this save was made on; when it is gone (signed out), the answer is ignored.
  const owner = s.query;
  const current = () => s.query === owner && entryOf(qc, co) === owner;
  api.client.me.prefs
    .put({ params: { co, key: "home_layout" }, body: { value } })
    .then(
      () => {
        if (!current()) return;
        s.stored = value;
        for (const c of callbacks) c.onSuccess?.();
      },
      (e: unknown) => {
        if (!current()) return;
        // Nothing newer waiting: show what is stored again.
        if (!s.next) qc.setQueryData(p1Keys.pref(co, "home_layout"), s.stored);
        for (const c of callbacks) c.onError?.(e);
      },
    )
    .finally(() => {
      // A queue restarted for a new cache entry is not this save's to touch.
      if (s.query !== owner) return;
      s.busy = false;
      const next = s.next;
      s.next = null;
      // The cache this queue served is gone (signed out): drop what waits.
      if (!current()) return;
      if (next) send(qc, api, co, s, next.value, next.callbacks);
    });
}

export function useHomeLayout(
  api: Api,
  co: string,
  /** The signed-in person (queues are per person). */
  person: string,
  /** False for a person who keeps no prefs here (a client viewer): the default, read only. */
  enabled: boolean,
): {
  value: HomeLayoutPref;
  /** The stored value has been read: edits may start. */
  ready: boolean;
  /** The read failed: no edits (they would overwrite an unread layout) until `retry`. */
  failed: boolean;
  retry: () => void;
  /**
   * Applies `update` to the newest value (an edit made before the screen re-rendered still sees
   * the one before it), shows the result and saves it.
   */
  save: (update: (current: HomeLayoutPref) => HomeLayoutPref, on?: Callbacks) => void;
} {
  const qc = useQueryClient();
  const queryKey = p1Keys.pref(co, "home_layout");
  const queue = queueFor(qc, `${person}:${co}`);
  const q = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      // A save of this cache entry pending or queued: keep the edited value, never an older one.
      const saving = () => (queue.busy || queue.next !== null) && entryOf(qc, co) === queue.query;
      const edited = () =>
        (qc.getQueryData<HomeLayoutPref | null>(queryKey) ?? null) as HomeLayoutPref | null;
      if (saving()) return edited();
      const edits = queue.edits;
      const value = ((await api.client.me.prefs.get({ params: { co, key: "home_layout" } }))
        .value ?? null) as HomeLayoutPref | null;
      if (saving()) return edited();
      // Only a read that was neither cancelled nor overtaken by an edit is what the API holds.
      if (!signal.aborted && queue.edits === edits && entryOf(qc, co) === queue.query)
        queue.stored = value;
      return value;
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled,
  });
  const ready = enabled && q.isSuccess;
  return {
    value: q.data ?? {},
    ready,
    failed: enabled && q.isError,
    retry: () => void q.refetch(),
    save: (update, on) => {
      if (!ready) return;
      const entry = entryOf(qc, co);
      // A new cache entry (a fresh session): a fresh queue for it.
      if (queue.query !== entry) {
        queue.query = entry;
        queue.next = null;
        queue.busy = false;
        queue.stored = (qc.getQueryData<HomeLayoutPref | null>(queryKey) ??
          null) as HomeLayoutPref | null;
      }
      const current = (qc.getQueryData<HomeLayoutPref | null>(queryKey) ??
        null) as HomeLayoutPref | null;
      const next = update(current ?? {});
      queue.edits++;
      void qc.cancelQueries({ queryKey });
      qc.setQueryData(queryKey, next);
      const callbacks = on ? [on] : [];
      if (queue.busy)
        queue.next = { value: next, callbacks: [...(queue.next?.callbacks ?? []), ...callbacks] };
      else send(qc, api, co, queue, next, callbacks);
    },
  };
}
